import type { CSSProperties, ComponentType, SVGProps, Ref, MouseEventHandler } from 'react'

export interface IconProps {
  width?: number | string
  height?: number | string
  color?: string
  hovercolor?: string
  margin?: string
  cursor?: string
  className?: string
  style?: CSSProperties
  ref?: Ref<SVGSVGElement>
  onClick?: MouseEventHandler
  [key: string]: unknown
}

type SvgComponent = ComponentType<SVGProps<SVGSVGElement>>

interface IconDefaults {
  defaultWidth?: string
  defaultHeight?: string
  cursor?: string
  extraStyle?: CSSProperties
  colorTarget?: 'stroke' | 'fill'
}

export function createIconComponent(
  SvgComponent: SvgComponent,
  displayName: string,
  defaults: IconDefaults = {},
) {
  const {
    defaultWidth = 'var(--appTableIconSize)',
    defaultHeight = 'var(--appTableIconSize)',
    cursor: defaultCursor,
    extraStyle,
    colorTarget = 'stroke',
  } = defaults

  const strokeCls = '[&_path]:stroke-(--icon-color)'
  const fillCls = '[&_path]:fill-(--icon-color)'
  const hoverStrokeCls = 'hover:[&_path]:stroke-(--icon-hover-color)'
  const hoverFillCls = 'hover:[&_path]:fill-(--icon-hover-color)'
  const colorCls = colorTarget === 'fill' ? fillCls : strokeCls
  const hoverCls = colorTarget === 'fill' ? hoverFillCls : hoverStrokeCls

  const IconComponent = ({
    ref,
    width,
    height,
    color,
    hovercolor,
    margin,
    cursor,
    className,
    style,
    ...props
  }: IconProps) => (
    <SvgComponent
      ref={ref}
      className={`${color ? colorCls : ''} ${hovercolor ? hoverCls : ''} ${className || ''}`}
      style={{
        width: width || defaultWidth,
        height: height || defaultHeight,
        cursor: cursor || defaultCursor || undefined,
        margin: margin || undefined,
        '--icon-color': color || undefined,
        '--icon-hover-color': hovercolor || undefined,
        ...extraStyle,
        ...style,
      } as CSSProperties}
      {...props}
    />
  )
  IconComponent.displayName = displayName
  return IconComponent
}

export function createSimpleIcon(
  SvgComponent: SvgComponent,
  displayName: string,
  defaults: IconDefaults = {},
) {
  const {
    defaultWidth = 'var(--appTableIconSize)',
    defaultHeight = 'var(--appTableIconSize)',
    cursor: defaultCursor,
  } = defaults

  const IconComponent = ({
    ref,
    width,
    height,
    color,
    margin,
    cursor,
    className,
    style,
    ...props
  }: IconProps) => (
    <SvgComponent
      ref={ref}
      className={className}
      style={{
        width: width || defaultWidth,
        height: height || defaultHeight,
        cursor: cursor || defaultCursor || undefined,
        margin: margin || undefined,
        color: color || undefined,
        ...style,
      } as CSSProperties}
      {...props}
    />
  )
  IconComponent.displayName = displayName
  return IconComponent
}

export function createColorStrokeIcon(
  SvgComponent: SvgComponent,
  displayName: string,
) {
  const IconComponent = ({
    ref,
    width,
    height,
    color,
    className,
    style,
    ...props
  }: IconProps) => (
    <SvgComponent
      ref={ref}
      className={`${color ? '[&_path]:stroke-(--icon-color)' : ''} ${className || ''}`}
      style={{
        width: width || 'var(--appTableIconSize)',
        height: height || 'var(--appTableIconSize)',
        '--icon-color': color || undefined,
        ...style,
      } as CSSProperties}
      {...props}
    />
  )
  IconComponent.displayName = displayName
  return IconComponent
}

export function createColorFillIcon(
  SvgComponent: SvgComponent,
  displayName: string,
) {
  const IconComponent = ({
    ref,
    width,
    height,
    color,
    className,
    style,
    ...props
  }: IconProps) => (
    <SvgComponent
      ref={ref}
      className={`${color ? '[&_path]:fill-(--icon-color)' : ''} ${className || ''}`}
      style={{
        width: width || 'var(--appTableIconSize)',
        height: height || 'var(--appTableIconSize)',
        '--icon-color': color || undefined,
        ...style,
      } as CSSProperties}
      {...props}
    />
  )
  IconComponent.displayName = displayName
  return IconComponent
}
